import type { TutorialStep } from "../../constants/activationContent";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { getTutorialSteps } from "../../constants/activationContent";
import { TutorialColumn } from "./TutorialColumn";

interface EditorWelcomeModalProps {
  isOpen: boolean;
  onNext: () => void;
}

export default function EditorWelcomeModal({
  isOpen,
  onNext,
}: EditorWelcomeModalProps) {
  const steps = React.useMemo(
    () => getTutorialSteps({ tutorialLocation: "editor" }),
    [],
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 hidden bg-black/50 md:block"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-0 right-0 z-50 hidden p-4 md:flex"
          >
            <WelcomeContent steps={steps} onNext={onNext} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface WelcomeContentProps {
  steps: TutorialStep[];
  onNext: () => void;
}

function WelcomeContent({ steps, onNext }: WelcomeContentProps) {
  return (
    <div className="flex w-full max-w-[1000px] flex-col gap-4 rounded-xl bg-black p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h2 className="header-h4 text-white">Welcome to the Chat Editor!</h2>
          <p className="text-base text-white/60">Here are 3 ways to edit.</p>
        </div>
        <Button
          variant="secondary"
          size="base"
          onClick={onNext}
          endEnhancer={<ArrowRight />}
        >
          Next
        </Button>
      </div>

      {/* Three columns with videos/images */}
      <div className="flex divide-x divide-white/20 overflow-hidden rounded-xl">
        {steps.map((step, index) => (
          <TutorialColumn
            key={step.id}
            step={step}
            isFirst={index === 0}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
